import React from 'react';
import './index.css';
import { createRoot } from 'react-dom/client';
import { App } from './App';

/**
 * Everything in this file runs BEFORE React renders. On a slow mobile
 * connection the boot sequence is the whole ballgame: whatever we can start
 * downloading here overlaps with React booting, instead of queueing behind it.
 */

// The hero crops. Duplicated from HeroSection so we can start fetching the
// right one during boot rather than after React has mounted and rendered.
const HERO_IMAGE_DESKTOP = 'https://cdn.magicpatterns.com/uploads/7pbR7cAfBZEjKm75n961a2/Untitled_design-2.jpg';
const HERO_IMAGE_MOBILE = 'https://cdn.magicpatterns.com/uploads/5p8SAadpusRjjUJUmYipsD/Untitled_design-3.jpg';
const HERO_BREAKPOINT = '(min-width: 768px)';
function appendLink(attrs: Record<string, string>) {
  const link = document.createElement('link');
  Object.entries(attrs).forEach(([key, value]) => {
    link.setAttribute(key, value);
  });
  document.head.appendChild(link);
  return link;
}

/**
 * Resolve the theme synchronously, before the first paint.
 *
 * Previously the `dark` class was applied inside a React effect, i.e. AFTER
 * the browser had already painted a light page — so dark-mode visitors saw a
 * white flash that then animated to dark. Setting the class here means the very
 * first painted frame is already correct.
 *
 * `no-transitions` suppresses colour transitions for the first frame so the
 * initial render can never animate into place; App removes it once mounted.
 */
function applyThemeBeforePaint() {
  const root = document.documentElement;
  root.classList.add('no-transitions');
  try {
    if (localStorage.getItem('sf-theme') === 'dark') {
      root.classList.add('dark');
    }
  } catch {
    // localStorage can throw in private/blocked contexts — light mode is a fine
    // default, and the site must still boot.
  }
}

/**
 * Inter, loaded as a single variable-range request (wght 300..800) instead of
 * six discrete weights. Preconnecting warms the DNS/TLS handshake to Google's
 * two font origins, and preloading the stylesheet collapses part of the
 * two-hop chain (stylesheet on one domain, font file on another).
 *
 * `display=swap` keeps text visible while the font is in flight; the metric
 * matched fallback in index.css is what stops that swap from reflowing the page.
 */
function loadFonts() {
  if (document.head.querySelector('link[data-inter-font]')) return;
  const href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300..800&display=swap';
  appendLink({
    rel: 'preconnect',
    href: 'https://fonts.googleapis.com'
  });
  appendLink({
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossorigin: 'anonymous'
  });
  appendLink({
    rel: 'preload',
    as: 'style',
    href
  });
  appendLink({
    rel: 'stylesheet',
    href,
    'data-inter-font': 'true'
  });
}

/**
 * The hero photo is the LCP element. Its URL lives inside a React component,
 * so without this the browser cannot even discover it until the bundle has
 * downloaded, parsed and rendered — it ends up last in the chain instead of
 * downloading in parallel from the start.
 *
 * The `media` attributes mirror the <picture> element in HeroSection, so only
 * the crop that will actually be displayed gets fetched. We never pull both.
 */
function preloadHeroImage() {
  appendLink({
    rel: 'preconnect',
    href: 'https://cdn.magicpatterns.com'
  });
  appendLink({
    rel: 'preload',
    as: 'image',
    href: HERO_IMAGE_MOBILE,
    media: `not all and ${HERO_BREAKPOINT}`,
    fetchpriority: 'high'
  });
  appendLink({
    rel: 'preload',
    as: 'image',
    href: HERO_IMAGE_DESKTOP,
    media: HERO_BREAKPOINT,
    fetchpriority: 'high'
  });
}
applyThemeBeforePaint();
loadFonts();
preloadHeroImage();
const container = document.getElementById('root');
if (container) {
  createRoot(container).render(<App data-id="element-488" data-mp="1uwm54aup32d0" />);
}